import React from 'react';
import {Linking, TouchableOpacity, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCAccordion from '@/Atoms/CCAccordion';
import Spacer from '@/Atoms/Spacer';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';

const SUPPORT_WHATSAPP_LINK = 'https://wa.me/918047358900';

export interface FaqItem {
  question?: string | null;
  help_text: string;
  deflectable: boolean;
  link_url?: string | null;
  link_label?: string | null;
}

interface FaqStepProps {
  category: string;
  subCategory: string;
  faqs: FaqItem[];
  onBack: () => void;
  onDismiss: () => void;
  // link_url values that aren't http(s) are treated as an in-app screen
  // name (e.g. "Renewal") and routed here instead of Linking.openURL --
  // lets a FAQ row nudge straight into a real in-app flow, not just an
  // external link.
  onNavigate?: (screen: string) => void;
}

// Each FAQ item is fully self-contained: its own content, its own link (if
// any), and its own action row. "Got it" only appears when this specific
// item can be self-resolved (deflectable) -- otherwise "Chat with us" is
// the only action, since nudging someone toward a dismiss button on a FAQ
// that explicitly needs a human doesn't make sense.
const FaqItemBody: React.FC<{
  item: FaqItem;
  onDismiss: () => void;
  onNavigate?: (screen: string) => void;
}> = ({item, onDismiss, onNavigate}) => (
  <View>
    <CCText fontSize={FontSizes[14]} style={{lineHeight: 20}}>
      {item.help_text}
    </CCText>
    {item.link_url ? (
      <>
        <Spacer spacing={vs(8)} />
        <TouchableOpacity
          onPress={() =>
            /^https?:\/\//.test(item.link_url!)
              ? Linking.openURL(item.link_url!)
              : onNavigate?.(item.link_url!)
          }>
          <CCText
            fontSize={FontSizes[13]}
            color={CCColors?.Green}
            style={{textDecorationLine: 'underline'}}>
            {item.link_label || 'View full policy'}
          </CCText>
        </TouchableOpacity>
      </>
    ) : null}
    <Spacer spacing={vs(14)} />
    <View style={{flexDirection: 'row', alignItems: 'stretch', gap: 10}}>
      {item.deflectable ? (
        <CCButton
          style={{
            flex: 1,
            backgroundColor: CCColors?.Buttons?.Background,
            borderColor: CCColors?.Buttons?.Background,
          }}
          titleStyle={{color: CCColors?.TextColor?.Primary}}
          onPress={onDismiss}>
          Got it
        </CCButton>
      ) : null}
      <CCButton
        style={{flex: 1, backgroundColor: '#25D366', borderColor: '#25D366'}}
        onPress={() => Linking.openURL(SUPPORT_WHATSAPP_LINK)}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}>
          <Ionicons name="logo-whatsapp" size={15} color="#fff" />
          <CCText fontWeight="bold" fontSize={FontSizes[14]} color="#fff">
            Chat with us
          </CCText>
        </View>
      </CCButton>
    </View>
  </View>
);

const FaqStep: React.FC<FaqStepProps> = ({
  category,
  subCategory,
  faqs,
  onBack,
  onDismiss,
  onNavigate,
}) => {
  const isAccordion = faqs.length > 1 || faqs.some(f => !!f.question);
  // faqs should never be empty here -- index.tsx only reaches this step when
  // helpTextMap[subCategoryId] is non-empty -- but guard anyway rather than
  // relying on the caller: faqs[0] on an empty array is undefined, not null,
  // and single.deflectable below would throw.
  const single = !isAccordion && faqs.length > 0 ? faqs[0] : null;

  return (
    <View style={{paddingHorizontal: 4}}>
      <TouchableOpacity
        onPress={onBack}
        style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
        <Ionicons
          name="chevron-back"
          size={16}
          color={CCColors?.TextColor?.Grey}
        />
        <CCText fontSize={FontSizes[13]} color={CCColors?.TextColor?.Grey}>
          Change category
        </CCText>
      </TouchableOpacity>
      <Spacer spacing={vs(16)} />
      <CCText fontSize={FontSizes[12]} color={CCColors?.TextColor?.Grey}>
        {category} → {subCategory}
      </CCText>
      <Spacer spacing={vs(10)} />

      {single ? (
        <View
          style={{
            backgroundColor: updateColorWithOpacity(CCColors?.Green, 0.07),
            borderLeftWidth: 3,
            borderLeftColor: CCColors?.Green,
            borderRadius: 10,
            padding: 16,
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
            <Ionicons
              name={single.deflectable ? 'bulb-outline' : 'person-outline'}
              size={20}
              color={CCColors?.Green}
            />
            <CCText fontWeight="bold" fontSize={FontSizes[15]}>
              Before you continue
            </CCText>
          </View>
          <Spacer spacing={vs(10)} />
          <FaqItemBody
            item={single}
            onDismiss={onDismiss}
            onNavigate={onNavigate}
          />
        </View>
      ) : faqs.length > 0 ? (
        <CCAccordion
          paddingVertical={0}
          sections={faqs.map(f => ({
            title: f.question || f.help_text,
            renderContent: () => (
              <FaqItemBody
                item={f}
                onDismiss={onDismiss}
                onNavigate={onNavigate}
              />
            ),
          }))}
        />
      ) : null}
    </View>
  );
};

export default FaqStep;
